///Users/bobbygilbert/Documents/GitHub/TPCTrust-platform/app/[locale]/(routes)/leads/components/UpdateLeadForm.tsx

"use client";

import { useState } from "react";
import { z } from "zod";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { format } from "date-fns";
import useSWR from "swr";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronsUpDown } from "lucide-react";

import fetcher from "@/lib/fetcher";
import SuspenseLoading from "@/components/loadings/suspense";

type NewTaskFormProps = {
  initialData: any;
  setOpen: (value: boolean) => void;  
};


const formSchema = z.object({
  id: z.string().min(5).max(30),
  first_name: z.string(),
  middle_name: z.string().optional(),
  last_name: z.string(),
  ssn: z.string().regex(/^\d{3}-\d{2}-\d{4}$/, "Invalid SSN format, should be '123-45-6789'"),
  company: z.string().nullable().optional(),
  phone: z.string(),
  email: z.string().email(),
  advisorName: z.string().optional(),
  advisorEmail: z.string().optional(),
  isReturningInvestor: z.boolean(),
  ownsEquipmentCompany: z.boolean(),
  dlfEngagementForLLC: z.boolean(),
  preferredBusinessName: z.string(),
  alternateBusinessName: z.string(),
  stateOfFormation: z.string(),
  equipmentUsagePlan: z.string(),
  investmentAmount: z.number(),
  downPaymentPercentage: z.number(),
  businessType: z.string(),
  businessFoundingDate: z.date(),
  percentageOwnership: z.number(),
  equipmentLLCName: z.string(),
  equipmentLLCEIN: z.string(),
  equipmentLLCAddress: z.string(),
  downPaymentAmount: z.number(),
  cardNumber: z.string(),
  cardName: z.string(),
  expirationDate: z.date(),
  cvv: z.string(),
  city: z.string(),
  postal_code: z.string(),
  state: z.string(),
  street_address: z.string(),
  business_owner: z.string().optional().nullable(),
  assetInterests: z.array(z.enum(["equipment", "open_to_others", "digital_advertising_screens", "gpu_data_servers"])).optional(),
});

type NewLeadFormValues = z.infer<typeof formSchema>;

export function UpdateLeadForm({ initialData, setOpen }: NewTaskFormProps) {
  const router = useRouter();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: accounts, isLoading: isLoadingAccounts } = useSWR(
    "/api/crm/account",
    fetcher
  );

  const { data: users, isLoading: isLoadingUsers } = useSWR(
    "/api/user",
    fetcher
  );

  const form = useForm<NewLeadFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...initialData,
      first_name: initialData.firstName || "",
      middle_name: initialData.middleName || "",
      last_name: initialData.lastName || "",
      ssn: initialData.ssn || "",
      phone: initialData.phone || "",
      email: initialData.email || "",
      street_address: initialData.streetAddress || "",
      postal_code: initialData.postalCode || "",
      state: initialData.state || "",
      city: initialData.city || "",
      country: initialData.country || "",
      advisorName: initialData.advisorName || "",
      advisorEmail: initialData.advisorEmail || "",
      isReturningInvestor: initialData.isReturningInvestor || false,
      ownsEquipmentCompany: initialData.ownsEquipmentCompany || false,
      dlfEngagementForLLC: initialData.dlfEngagementForLLC || false,
      preferredBusinessName: initialData.preferredBusinessName || "",
      alternateBusinessName: initialData.alternateBusinessName || "",
      stateOfFormation: initialData.stateOfFormation || "",
      equipmentUsagePlan: initialData.equipmentUsagePlan || "",
      investmentAmount: initialData.investmentAmount || 0,
      downPaymentPercentage: initialData.downPaymentPercentage || 0,
      businessType: initialData.businessType || "",
      businessFoundingDate: initialData.businessFoundingDate || new Date(),
      percentageOwnership: initialData.percentageOwnership || 0,
      equipmentLLCName: initialData.equipmentLLCName || "",
      equipmentLLCEIN: initialData.equipmentLLCEIN || "",
      equipmentLLCAddress: initialData.equipmentLLCAddress || "",
      downPaymentAmount: initialData.downPaymentAmount || 0,
      cardNumber: initialData.cardNumber || "",
      cardName: initialData.cardName || "",
      expirationDate: initialData.expirationDate || new Date(),
      cvv: initialData.cvv || "",
      business_owner: initialData.business_owner === "yes" ? "yes" : "no",
      assetInterests: initialData.assetInterests || [],
    },
  });
  type AssetOptionValue = NonNullable<NewLeadFormValues['assetInterests']>[number];
  const assetOptions: { label: string; value: AssetOptionValue }[] = [
    { label: "Equipment", value: "equipment" },
    { label: "Open to others", value: "open_to_others" },
    { label: "Digital Advertising Screens", value: "digital_advertising_screens" },
    { label: "GPU Data Servers", value: "gpu_data_servers" },
  ];

  const onSubmit = async (data: NewLeadFormValues) => {
    setIsLoading(true);
    try {
      await axios.put("/api/crm/leads", data);
      toast({
        title: "Success",
        description: "Lead updated successfully",
      });
    } catch (error: any) {
      const fallbackMessage =
        error?.response?.data?.message ??
        error?.response?.data ??
        error?.message ??
        "An unexpected error occurred.";
      toast({
        variant: "destructive",
        title: "Error",
        description:
          typeof fallbackMessage === "string"
            ? fallbackMessage
            : JSON.stringify(fallbackMessage),
      });
    } finally {
      setIsLoading(false);
      setOpen(false);      
      router.refresh();
    }
  };

  if (isLoadingUsers || isLoadingAccounts) {
    return (
      <div>
        <SuspenseLoading />
      </div>
    );
  }

  if (!users || !initialData) {
    return <div>Something went wrong, there is no data for form</div>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="h-full px-10">
        <div className="w-[800px] text-sm">
          <div className="pb-5 space-y-2">
            {/* Personal Information */}
            <h2 className="mb-4 text-lg font-semibold">Personal Information</h2>



            <div className="grid grid-cols-2 gap-4">



            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First name</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} {...field} placeholder="John" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="middle_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Middle name</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} {...field} placeholder="Doe" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last name</FormLabel>
                  <FormControl>
                    <Input disabled={isLoading} {...field} placeholder="Doe" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="ssn"
              rules={{
                required: "SSN is required",
                pattern: {
                  value: /^\d{3}-\d{2}-\d{4}$/,
                  message: 'Invalid SSN format, should be "123-45-6789"',
                },
              }}
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>SSN</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="123-45-6789" />
                  </FormControl>
                  {fieldState.error && (
                    <p style={{ color: "red" }}>{fieldState.error.message}</p>
                  )}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="assetInterests"
              render={({ field }) => {
                const currentValue = (Array.isArray(field.value) ? field.value : []) as AssetOptionValue[];
                const formattedSelection = currentValue
                  .map((value) => assetOptions.find((option) => option.value === value)?.label ?? value)
                  .join(", ");

                return (
                  <FormItem className="col-span-2">
                    <FormLabel>What asset is this investor looking to purchase?</FormLabel>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            "w-full justify-between",
                            currentValue.length === 0 && "text-muted-foreground"
                          )}
                        >
                          {currentValue.length > 0 ? formattedSelection : "Select asset type(s)"}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-64">
                        {assetOptions.map((option) => (
                          <DropdownMenuCheckboxItem
                            key={option.value}
                            checked={currentValue.includes(option.value)}
                            onCheckedChange={(checked) => {
                              const isChecked = checked === true;
                              if (isChecked) {
                                if (!currentValue.includes(option.value)) {
                                  field.onChange([...currentValue, option.value]);
                                }
                              } else {
                                field.onChange(currentValue.filter((item) => item !== option.value));
                              }
                            }}
                          >
                            {option.label}
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

</div>
<Separator />
            {/* Contact Information */}
            <h2 className="mt-8 mb-4 text-lg font-semibold">
              Contact Information
            </h2>


            <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>E-mail</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      placeholder="johny@domain.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      placeholder="+1234567890"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
</div>

<Separator />
            {/* Address Information */}
            <h2 className="mt-8 mb-4 text-lg font-semibold">
              Address Information
            </h2>


            <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="street_address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Street Address</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      {...field}
                      placeholder="Street Address"
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      {...field}
                      placeholder="City"
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      {...field}
                      placeholder="State"
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="postal_code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Postal Code</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      {...field}
                      placeholder="Postal Code"
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
</div>


<Separator />
            {/* Business Information */}
            <h2 className="mt-8 mb-4 text-lg font-semibold">
              Business Information
            </h2>


            <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="business_owner"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Business Owner</FormLabel>
                  <FormControl>
                    <Select
                      disabled={isLoading}
                      onValueChange={field.onChange}
                      value={field.value || ""}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="preferredBusinessName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Preferred Business Name</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      {...field}
                      placeholder="Preferred Business Name"
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="alternateBusinessName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Alternate Business Name</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      {...field}
                      placeholder="Alternate Business Name"
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="stateOfFormation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State of Formation</FormLabel>
                  <FormControl>
                    <Input
                      disabled={isLoading}
                      {...field}
                      placeholder="State of Formation"
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="businessFoundingDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Business Founding Date</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      disabled={isLoading}
                      {...field}
                      value={
                        field.value
                          ? field.value.toISOString().slice(0, 10)
                          : ""
                      }
                      onChange={(e) => field.onChange(new Date(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
</div>


<Separator />
            {/* Investment Information */}
            <h2 className="mt-8 mb-4 text-lg font-semibold">
              Investment Information
            </h2>


            <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="isReturningInvestor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Is Returning Investor</FormLabel>
                  <FormControl>
                    <input
                      type="checkbox"
                      disabled={isLoading}
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      />

</FormControl>
<FormMessage />
</FormItem>
)}
/>
<FormField
control={form.control}
name="investmentAmount"
render={({ field }) => (
<FormItem>
<FormLabel>Investment Amount</FormLabel>
<FormControl>
<Input
type="number"
disabled={isLoading}
{...field}
placeholder="0"
onChange={(e) =>
field.onChange(parseFloat(e.target.value) || 0)
}
/>
</FormControl>
<FormMessage />
</FormItem>
)}
/>
<FormField
control={form.control}
name="downPaymentPercentage"
render={({ field }) => (
<FormItem>
<FormLabel>Down Payment Percentage</FormLabel>
<FormControl>
<Input
type="number"
disabled={isLoading}
{...field}
placeholder="0"
onChange={(e) =>
field.onChange(parseFloat(e.target.value) || 0)
}
/>
</FormControl>
<FormMessage />
</FormItem>
)}
/>
<FormField
control={form.control}
name="downPaymentAmount"
render={({ field }) => (
<FormItem>
<FormLabel>Down Payment Amount</FormLabel>
<FormControl>
<Input
type="number"
disabled={isLoading}
{...field}
placeholder="0"
onChange={(e) =>
field.onChange(parseFloat(e.target.value) || 0)
}
/>
</FormControl>
<FormMessage />
</FormItem>
)}
/>

</div>
<Separator />
{/* Equipment LLC Information */}
<h2 className="mt-8 mb-4 text-lg font-semibold">
          Equipment LLC Information
        </h2>


        <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="ownsEquipmentCompany"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Owns Equipment Company</FormLabel>
              <FormControl>
                <input
                  type="checkbox"
                  disabled={isLoading}
                  checked={field.value || false}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="dlfEngagementForLLC"
          render={({ field }) => (
            <FormItem>
              <FormLabel>DLF Engagement for LLC</FormLabel>
              <FormControl>
                <input
                  type="checkbox"
                  disabled={isLoading}
                  checked={field.value || false}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="equipmentUsagePlan"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Equipment Usage Plan</FormLabel>
              <FormControl>
                <Input
                  disabled={isLoading}
                  {...field}
                  placeholder="Equipment Usage Plan"
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="equipmentLLCName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Equipment LLC Name</FormLabel>
              <FormControl>
                <Input
                  disabled={isLoading}
                  {...field}
                  placeholder="Equipment LLC Name"
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="equipmentLLCEIN"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Equipment LLC EIN</FormLabel>
              <FormControl>
                <Input
                  disabled={isLoading}
                  {...field}
                  placeholder="Equipment LLC EIN"
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="equipmentLLCAddress"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Equipment LLC Address</FormLabel>
              <FormControl>
                <Input
                  disabled={isLoading}
                  {...field}
                  placeholder="Equipment LLC Address"
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
</div>
<div className="mt-4 mb-4">
              <Separator />
            </div>
        {/* Payment Information */}
        <h2 className="mt-8 mb-4 text-lg font-semibold">
          Payment Information
        </h2>

        <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="cardNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Card Number</FormLabel>
              <FormControl>
                <Input
                  disabled={isLoading}
                  {...field}
                  placeholder="Card Number"
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="cardName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Card Name</FormLabel>
              <FormControl>
                <Input
                  disabled={isLoading}
                  {...field}
                  placeholder="Card Name"
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="expirationDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Expiration Date</FormLabel>
              <FormControl>
                <Input
                  type="date"
                  disabled={isLoading}
                  {...field}
                  value={
                    field.value
                      ? field.value.toISOString().slice(0, 10)
                      : ""
                  }
                  onChange={(e) => field.onChange(new Date(e.target.value))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />



        <FormField
          control={form.control}
          name="cvv"
          render={({ field }) => (
            <FormItem>
              <FormLabel>CVV</FormLabel>
              <FormControl>
                <Input
                  disabled={isLoading}
                  {...field}
                  placeholder="CVV"
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
          </div>
      </div>
    </div>
    <div className="grid gap-2 py-5">
      <Button className="px-4 py-2 text-white rounded bg-gradient-to-b from-blue-500 to-blue-800" disabled={isLoading} type="submit">
        {isLoading ? (
          <span className="flex items-center animate-pulse">
            Saving data ...
          </span>
        ) : (

          "Update profile"
        )}
      </Button>
    </div>
  </form>
</Form>
);
}
